import { Select, Spin } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { socketEmitEvent } from '../../redux/actions';
import { LoadingOutlined } from '@ant-design/icons';

const debounce = (func, timeout = 400) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
};

const LazyloadingSelect = ({
  value,
  defaultOption,
  onChange,
  size = 10,
  serviceId,
  reduxVariableName,
  convertOptionData,
  searchField,
  refreshKey,
  selectProps,
}) => {
  const searchKey = useRef('');
  const page = useRef(0);
  const dispatch = useDispatch();
  const [listOption, setListOption] = useState({
    page: -1,
    total: 1,
    data: [defaultOption],
  });
  const [isLoading, setIsLoading] = useState(false);
  const {
    namespace,
    isAuth,
    [reduxVariableName]: paginationOptionData,
  } = useSelector((state) => state);

  const [key, setKey] = useState(null); // trigger refresh scroll

  useEffect(() => {
    if (paginationOptionData) {
      let optionsInit = [];
      if (
        defaultOption?.label
          ?.toLowerCase()
          .includes(searchKey.current.toLowerCase())
      ) {
        optionsInit = [defaultOption];
      }
      setListOption({
        page: paginationOptionData.page,
        total: paginationOptionData.total + optionsInit.length,
        data: (paginationOptionData.page > listOption.page
          ? listOption.data
          : optionsInit
        ).concat(convertOptionData(paginationOptionData)),
      });
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationOptionData]);

  const getPaginationOptionData = useCallback(() => {
    setIsLoading(true);
    dispatch(
      socketEmitEvent({
        appServiceType: serviceId,
        requestId: new Date().getTime(),
        body: JSON.stringify({
          namespace,
          [searchField]: searchKey.current,
          page: page.current,
          size,
          timeRequest: new Date().getTime(),
        }),
      })
    );
  }, [dispatch, namespace, serviceId, size, searchField]);

  const getPaginationOptionDataDebounce = debounce(() => {
    page.current += 1;
    getPaginationOptionData();
  }, 200);

  const refreshListOption = useCallback(() => {
    setKey(new Date().getTime());
    searchKey.current = '';
    page.current = 0;
    getPaginationOptionData();
  }, [getPaginationOptionData]);

  useEffect(() => {
    if (isAuth === 1 && namespace) {
      getPaginationOptionData();
    }
  }, [getPaginationOptionData, isAuth, namespace]);

  useEffect(() => {
    // trigger refresh list option from outside
    if (refreshKey && isAuth === 1 && namespace) {
      refreshListOption();
    }
  }, [
    getPaginationOptionData,
    refreshKey,
    refreshListOption,
    isAuth,
    namespace,
  ]);

  const handleSearch = debounce((inputValue) => {
    searchKey.current = inputValue.trim();
    page.current = 0;
    getPaginationOptionData();
  });

  const handleScrollToBottom = (e) => {
    const isBottom =
      Math.ceil(e.target.scrollTop) + e.target.clientHeight >=
      e.target.scrollHeight;
    if (isBottom && listOption.data.length < listOption.total) {
      getPaginationOptionDataDebounce();
    }
  };

  const handleChangeOption = (newOption) => {
    onChange(listOption.data.find((item) => item.value === newOption.value));
  };

  const handleSelect = () => {
    refreshListOption();
  };

  return (
    <Select
      showSearch
      labelInValue
      onChange={handleChangeOption}
      onPopupScroll={handleScrollToBottom}
      onSearch={handleSearch}
      getPopupContainer={(trigger) => trigger.parentNode}
      value={
        listOption.data.find((item) => item?.value === value?.value) || value // this can fix warning: not same lable
      }
      key={key}
      dropdownRender={(menu) => (
        <Spin
          spinning={isLoading}
          indicator={<LoadingOutlined style={{ fontSize: 30 }} spin />}
        >
          {menu}
        </Spin>
      )}
      showArrow={false}
      filterOption={false}
      onSelect={handleSelect}
      loading={isLoading}
      options={listOption.data.map((item) => ({
        label: item?.label,
        value: item?.value,
      }))}
      {...selectProps}
    />
  );
};

export default LazyloadingSelect;
